// Domain-specific GraphQL resource definitions for Kiddom API
// This file contains resource shape definitions for various domain entities

import type { ResourceQuery } from 'infrastructure/api'

export type ResourceTypes = 'districts' | 'users' | 'schools' | 'demos'

interface Resource {
  listQuery: ResourceQuery
}

const _pageParams = [
  { page_info: ['current_page', 'per_page', 'has_next_page'] },
  'total_count',
]

export const SchoolResource: Resource = {
  listQuery: {
    key: 'schools',
    fields: [
      {
        schools: [
          'id',
          'name',
          'is_verified',
          'created_at',
          'deleted_at',
          { district: ['id', 'name'] },
        ],
      },
    ],
  },
}

export const DistrictResource: Resource = {
  listQuery: {
    key: 'districts',
    fields: [
      { districts: ['id', 'name', 'created_at', 'is_verified', 'deleted_at'] },
    ],
  },
}

export const UserResource: Resource = {
  listQuery: {
    key: 'users',
    fields: [
      {
        users: [
          'id',
          'email',
          'name',
          'image_url',
          'roles',
          'created_at',
          'deleted_at',
          {
            school: ['id', 'name', { district: ['id', 'name'] }],
            classes: [{ class: ['id', 'alias'] }],
          },
        ],
      },
    ],
  },
}

export const DistrictSchoolResource: Resource = {
  listQuery: {
    key: 'districtSchools',
    fields: [
      {
        districtSchools: [
          'id',
          'name',
          'admin_count',
          'teacher_count',
          'student_count',
          'is_verified',
          'created_at',
          'deleted_at',
          { features: ['id', 'name', 'locked', 'free_trial'] },
        ],
      },
    ],
  },
}

export const PlatformFeatures: ResourceQuery = {
  key: 'platform_features',
  fields: ['id', 'name'],
}

export const DeleteSchool: ResourceQuery = {
  key: 'deleteSchool',
}

export const DeleteDistrict: ResourceQuery = {
  key: 'deleteDistrict',
}

export const DeleteManyUsers: ResourceQuery = {
  key: 'softDeleteUsersByIDs',
}

export const AddSchoolFeature: ResourceQuery = {
  key: 'addSchoolFeature',
  fields: ['id', { features: ['id', 'name', 'locked', 'free_trial'] }],
}

export const RemoveSchoolFeature: ResourceQuery = {
  key: 'removeSchoolFeature',
  fields: ['id', { features: ['id', 'name', 'locked', 'free_trial'] }],
}

export const SetProductPlatformFeature: ResourceQuery = {
  key: 'setProductPlatformFeature',
}

export const ResetCustomLessonsForClass: ResourceQuery = {
  key: 'resetCustomLessonsForClass',
}

export const UpdateDistrict: ResourceQuery = {
  key: 'updateDistrict',
}

export const UpdateSchoolSearchable: ResourceQuery = {
  key: 'updateSchoolSearchable',
}

export const UpdateSchoolInformation: ResourceQuery = {
  key: 'updateSchoolInformation',
  fields: [
    'id',
    'name',
    'is_verified',
    'created_at',
    'deleted_at',
    { district: ['id', 'name'] },
  ],
}

export const MasterInfoResource: ResourceQuery = {
  key: 'master',
  fields: ['tree_id', 'version', 'copied_trees'],
}

export const CurriculumTreeResource: ResourceQuery = {
  key: 'curriculaForTree',
  fields: [
    'id',
    'title',
    'created_at',
    'edited_at',
    'deleted',
    'tree_id',
    'tree_version',
    { schools: ['id', 'name'] },
  ],
}

export const MigrateVersionInfo: ResourceQuery = {
  key: 'migrateTreeVersion',
  fields: ['workflow_id', 'status'],
}

export const GetCurriculumInDistrictsByDistrictID: ResourceQuery = {
  key: 'curriculumInDistricts',
  fields: ['id', 'tree_id', 'tree_version', 'deleted', 'master_title'],
}

export const AddUser: ResourceQuery = {
  key: 'addUser',
}

export const DemoEnvironmentResource: Resource = {
  listQuery: {
    key: 'demos',
    fields: [
      {
        demos: [
          'id',
          'school_id',
          'token_duration_days',
          'date_created',
          'school_name',
          'date_deleted',
          'expiration_date',
          'demo_type',
        ],
      },
    ],
  },
}

export const UpdateUserSchool: ResourceQuery = {
  key: 'updateUserSchool',
}

export const MigrateSchoolCurriculaToDistrictLevel: ResourceQuery = {
  key: 'migrateCurriculaFromSchoolToDistrict',
}

export const FixDistrictAdminRoles: ResourceQuery = {
  key: 'districtAdminFix',
}

export const RemoveDuplicateIDFromSchool: ResourceQuery = {
  key: 'deleteDuplicateID',
}

export const AddCurricula: ResourceQuery = {
  key: 'addCurricula',
}

export const EditUser: ResourceQuery = {
  key: 'editUser',
}

export const ResetPassword: ResourceQuery = {
  key: 'resetPassword',
}

export const DeleteManyDemos: ResourceQuery = {
  key: 'deleteDemos',
}

export const GetLTIIntegration: ResourceQuery = {
  key: 'ltiIntegration',
  fields: ['issuer', 'baseurl', 'clientid', 'districtid', 'registrationid'],
}

export const CreateLTIRegistration: ResourceQuery = {
  key: 'createLTIRegistration',
}

export const CreateLTIIssuer: ResourceQuery = {
  key: 'createLTIIssuer',
}

export const GetLTIIssuers: ResourceQuery = {
  key: 'getLTIIssuers',
}

export const UpdateLTIRegistration: ResourceQuery = {
  key: 'updateLTIRegistration',
}

export const UpdateClassArchiveStatus: ResourceQuery = {
  key: 'updateClassArchiveStatus',
}

export const ResubmitAttempts: ResourceQuery = {
  key: 'resubmitAttempts',
}

export const GetSISDistrictInfo: ResourceQuery = {
  key: 'SISDistrictInfo',
  fields: [
    'sistype',
    'clientid',
    'secret',
    'host',
    'ingestionid',
    {
      synclimits: [
        'syncuser',
        'syncclass',
        'archiveclass',
        'offboardusers',
        'automatedoffboarding',
      ],
    },
  ],
}

export const SetSISDistrictInfo: ResourceQuery = {
  key: 'setSISDistrictInfo',
}

export const curriculumsAddedOnSchoolLevel: ResourceQuery = {
  key: 'curriculumsAddedOnSchoolLevel',
  fields: ['name', 'tree_id', 'school_count'],
}

export const ProductPlatformFeatures: ResourceQuery = {
  key: 'productPlatformFeatures',
  fields: [
    'is_editable',
    {
      product_platform_features: [
        'product_id',
        'platform_feature_id',
        'is_enabled',
        'created_at',
        'last_enabled_at',
        'last_disabled_at',
      ],
    },
  ],
}

export const CustomLessonCountForClass: ResourceQuery = {
  key: 'customLessonCountForClass',
}

export const LMSDistrictInfo: ResourceQuery = {
  key: 'LMSDistrictInfo',
  fields: [
    'integrationid',
    'districtid',
    'lmstype',
    'redirecturi',
    'refreshtoken',
    'dailysyncenabled',
  ],
}

export const UpdateLMSSettings: ResourceQuery = {
  key: 'updateLMSSettings',
}

export const TKATEventInfo: ResourceQuery = {
  key: 'TKATEventInfo',
  fields: [
    'id',
    'initiator_id',
    'target_type',
    'target_id',
    'action',
    'remarks',
    'creation_date',
    'completion_date',
  ],
}

export const CreateDemo: ResourceQuery = {
  key: 'createDemo',
}

export const ReinstateDistricts: ResourceQuery = {
  key: 'reinstateDistricts',
}

export const ReinstateDemos: ResourceQuery = {
  key: 'reinstateDemos',
}

export const ReinstateSchools: ResourceQuery = {
  key: 'reinstateSchools',
}

export const ReinstateUsers: ResourceQuery = {
  key: 'reinstateUsers',
}
